import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { authService } from '../../../services/authService';
import { useTheme } from '../../../contexts/ThemeContext';
import { AuthentipetLogo } from '../../../components/AuthentipetLogo';
import { AuthLayout } from './AuthLayout';
import { logger } from '../../../lib/logger';

interface AuthCallbackProps {
  onAuthSuccess: () => void;
}

export const AuthCallback: React.FC<AuthCallbackProps> = ({ onAuthSuccess }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      const result = await authService.handleAuthCallback();

      if (result.error) {
        logger.error('Auth callback error', new Error(result.error));
        setError(result.error);
        return;
      }

      if (result.hasSession) {
        logger.info('Magic link auth successful', { userId: result.userId });
        onAuthSuccess();
      }
    };

    handleCallback();
  }, [navigate, onAuthSuccess]);

  return (
    <AuthLayout className="items-center">
      <div className="flex justify-center mb-6">
        <div className="bg-white/10 p-2 rounded-full">
          <AuthentipetLogo size={80} />
        </div>
      </div>

      {error ? (
        <div className="text-center max-w-sm">
          <div className="p-4 rounded bg-red-500/10 border border-red-500/20 text-red-500 text-sm mb-4">
            {error}
          </div>
          <button
            onClick={() => navigate('/login')}
            className="text-storgia-cta-pink hover:text-storgia-cta-pink/80 font-medium"
          >
            Back to login
          </button>
        </div>
      ) : (
        <div className="text-center">
          <Loader2 className={`h-8 w-8 animate-spin mx-auto mb-4 ${theme === 'dark' ? 'text-storgia-green' : 'text-storgia-blurple'}`} />
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            Signing you in...
          </p>
        </div>
      )}
    </AuthLayout>
  );
};
